import React from 'react';

import { Card } from '@mui/material';

import { FormDataProvider } from './context/FormDataContext';
import CheckboxField from './fields/CheckboxField';
import GeoField from './fields/GeoField';
import RadioField from './fields/RadioField';
import TagField from './fields/TagField';
import TextFormField from './fields/TextFormField';
import * as S from './styles';
import { FieldWithSubmission } from './types/formTypes';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { useUser } from 'global/UserContext';
import { useFetchCompleteForm } from 'hooks/useFetchCompleteForm';

interface DynamicFormGroupProps {
  group_id: string;
}

const DynamicFormGroup: React.FC<DynamicFormGroupProps> = ({ group_id }) => {
  const { user } = useUser();
  const { formData, loading, error } = useFetchCompleteForm(group_id, user?.id || '');

  if (loading)
    return (
      <Card
        sx={{
          width: '100%',
          height: '15rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spin />
      </Card>
    );
  if (error)
    return (
      <Text size="small" color="red" bold textAlign="center">
        Erro ao carregar campos
      </Text>
    );

  if (!formData || formData.length === 0) {
    return (
      <Text size="small" color="red" bold textAlign="center">
        Nenhum formulário encontrado
      </Text>
    );
  }

  return (
    <div>
      {formData.map(form => (
        <div key={form.form_id} style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <Text size="large" bold color="textPrimary">
              {form.form_name}
            </Text>
          </div>
          {form.description && (
            <div style={{ marginBottom: '1rem' }}>
              <Text size="medium" color="textSecondary">
                {form.description}
              </Text>
            </div>
          )}
          <FormDataProvider fields={form.fields as FieldWithSubmission[]}>
            <S.ContainerForm>
              <form style={{ width: '100%' }}>
                <S.GridContainer
                  isTagsLayout={form.fields.some(
                    field => field.field_type === 'tags' || field.field_type === 'geo_state'
                  )}
                >
                  {form.fields.map(field => {
                    if (field.field_type === 'geo_state') {
                      return (
                        <GeoField
                          key={field.id}
                          form_id={field.form_id || form.form_id}
                          label={field.label}
                          is_required={field.is_required}
                          id={field.id}
                          field_type={field.field_type}
                          description={field.description}
                          extras={field.extras}
                          submission={field.submission}
                        />
                      );
                    }

                    if (field.field_type === 'tags') {
                      return (
                        <TagField
                          key={field.id}
                          form_id={field.form_id || form.form_id}
                          label={field.label}
                          is_required={field.is_required}
                          id={field.id}
                          field_type={field.field_type}
                          description={field.description}
                          extras={field.extras}
                          submission={field.submission}
                        />
                      );
                    }

                    if (field.field_type === 'textarea') {
                      return <TextFormField key={field.id} field={field as any} />;
                    }

                    if (field.field_type === 'dropdown') {
                      return <RadioField key={field.id} field={field as any} />;
                    }

                    if (field.field_type === 'checkbox') {
                      return <CheckboxField key={field.id} field={field as any} />;
                    }
                    return null;
                  })}
                </S.GridContainer>
              </form>
            </S.ContainerForm>
          </FormDataProvider>
        </div>
      ))}
    </div>
  );
};

export default DynamicFormGroup;
